import { useEffect, useRef } from "react";
import { useInView, useMotionValue, useSpring } from "framer-motion";
import { Check, Crown } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Reveal";

const Counter = ({ value, suffix }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const mv = useMotionValue(0);
  const spring = useSpring(mv, { duration: 2200, bounce: 0 });

  useEffect(() => {
    if (inView) mv.set(value);
  }, [inView, value, mv]);

  useEffect(() => {
    const unsub = spring.on("change", (v) => {
      if (ref.current) ref.current.textContent = `${Math.round(v)}${suffix}`;
    });
    return unsub;
  }, [spring, suffix]);

  return <span ref={ref}>0{suffix}</span>;
};

const GAINS = [
  "Visibility",
  "Recognition",
  "Confidence",
  "Networking opportunities",
  "Media exposure",
  "Fashion & pageant experience",
  "Professional development",
  "National & international exposure",
];

export default function About({ stats }) {
  const aboutVideoRef = useRef(null);

  useEffect(() => {
    const v = aboutVideoRef.current;
    if (v) {
      v.muted = true;
      const p = v.play();
      if (p && p.catch) p.catch(() => {});
    }
  }, []);

  return (
    <section id="about" className="relative py-28 lg:py-36 spotlight-top" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-20 items-center">
          <div>
            <SectionHeading
              center={false}
              eyebrow="About Us"
              title={<>A House Where <span className="gold-text">Talent</span> Is Treasured</>}
              testId="about-heading"
            />
            <Reveal delay={0.15}>
              <p className="text-muted leading-relaxed -mt-8 mb-6" data-testid="about-description">
                HA Treasure House is a talent showcasing company dedicated to creating opportunities for aspiring
                models, performers, pageant participants, influencers and emerging talent.
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <p className="text-cream/80 leading-relaxed mb-8">
                We provide a premium platform where talent can gain everything the spotlight promises:
              </p>
            </Reveal>
            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3" data-testid="about-gains-list">
              {GAINS.map((g, i) => (
                <Reveal key={g} delay={0.05 * i}>
                  <li className="flex items-start gap-2.5 text-sm text-cream/85">
                    <Check size={16} className="text-gold mt-0.5 shrink-0" />
                    {g}
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>

          <Reveal delay={0.2} className="relative">
            <div className="relative">
              <div className="absolute -inset-4 border border-gold/25 rounded-sm translate-x-5 translate-y-5" />
              <video
                ref={aboutVideoRef}
                src="/assets/about.mp4"
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                aria-label="HA Treasure House animated logo reveal"
                className="relative w-full h-[480px] lg:h-[560px] object-contain bg-royal-900 rounded-sm gold-frame"
                data-testid="about-video"
              />
              <div className="absolute -bottom-6 -left-4 sm:-left-8 glass-card rounded-md px-6 py-4 flex items-center gap-3 animate-float">
                <Crown className="text-gold" size={26} />
                <div>
                  <p className="font-display text-lg gold-text font-semibold leading-none">Prestige</p>
                  <p className="text-xs text-muted mt-1 tracking-widest uppercase">In every detail</p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="mt-24 lg:mt-32 grid grid-cols-2 lg:grid-cols-4 gap-y-12 border-t border-gold/15 pt-14" data-testid="about-stats">
          {stats.map((s, i) => (
            <Reveal key={s.label} delay={0.1 * i} className="text-center">
              <p className="font-display text-5xl lg:text-6xl gold-text font-bold" data-testid={`stat-value-${i}`}>
                <Counter value={s.value} suffix={s.suffix} />
              </p>
              <p className="mt-3 text-muted text-sm tracking-[0.25em] uppercase">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
